"use client";

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { categorieActualiteColors } from '@/lib/colors';

export interface ArticleData {
  id: string;
  slug: string;
  titre: string;
  extrait: string;
  categorie: string;
  date: string;
  dateISO: string;
  imageUrl: string;
  imageAlt: string;
}

interface Props {
  articles: ArticleData[];
}

export default function NewsSection({ articles }: Props) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.news-card-anim');
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.classList.add('reveal-visible');
                card.classList.remove('reveal-hidden');
              }, i * 150);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="actualites" className="pt-20 pb-28 md:py-28 bg-white" aria-labelledby="news-title">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Informations & prévention
            </p>
            <h2
              id="news-title"
              className="text-3xl md:text-4xl font-semibold text-text-main mb-3"
              style={{ fontFamily: 'Poppins, sans-serif', letterSpacing: '-0.01em' }}>
              Actualités & conseils santé
            </h2>
            <span className="section-decorator section-decorator-left" aria-hidden="true" />
          </div>
          <Link
            href="/actualites"
            className="btn-secondary text-sm flex-shrink-0 self-start md:self-end"
            aria-label="Voir toutes les actualités et conseils santé">
            Toutes les actualités
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        {/* Articles grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" ref={sectionRef}>
          {articles.map((article, index) => {
            const colors = categorieActualiteColors[article.categorie] ?? { color: 'text-white', bg: 'bg-primary' };
            return (
              <article
                key={article.id}
                className={`news-card-anim reveal-hidden news-card bg-white border border-border rounded-card shadow-card card-hover overflow-hidden group delay-${(index + 1) * 100}`}
                aria-label={`Article : ${article.titre}`}>

                {/* Image */}
                <div className="overflow-hidden aspect-[16/9] relative">
                  <AppImage
                    src={article.imageUrl}
                    alt={article.imageAlt}
                    fill
                    className="news-card-img object-cover"
                    sizes="(max-width: 768px) 100vw, 33vw" />

                  <div
                    className={`absolute top-4 left-4 text-xs font-semibold px-3 py-1 rounded-full ${colors.bg} ${colors.color}`}
                    style={{ fontFamily: 'Poppins, sans-serif', letterSpacing: '0.03em' }}>
                    {article.categorie}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-text-muted text-xs mb-3 flex items-center gap-1.5" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <time dateTime={article.dateISO}>{article.date}</time>
                  </p>

                  <h3
                    className="text-base font-semibold text-text-main mb-3 leading-snug group-hover:text-primary transition-colors line-clamp-2"
                    style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {article.titre}
                  </h3>

                  <p
                    className="text-text-muted text-sm leading-relaxed mb-5 line-clamp-3"
                    style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {article.extrait}
                  </p>

                  <Link
                    href={`/actualites/${article.slug}`}
                    className="inline-flex items-center gap-2 text-primary text-sm font-medium hover:gap-3 transition-all duration-200"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                    aria-label={`Lire la suite : ${article.titre}`}>
                    Lire la suite
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
